import { actionItemStatus, Relations } from "@inrev/common";
import {
	ActionItem,
	useDeleteActionItem,
	useUpdateActionItem,
} from "@portal/api/action-item/admin";
import { cn } from "@portal/components/lib/utils";
import { CheckMarkBubble } from "@portal/components/ui/CheckMarkBubble";
import { QuickEditDateDisplay } from "@portal/components/ui/DateDisplay";
import { QuickEditRichTextDisplay } from "@portal/components/ui/RichTextDisplay";
import {
	useActionItemsSelection,
	useActionItemsSelectionDispatch,
} from "@portal/providers/ActionItemsSelectionProvider";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { FC } from "react";
import { AdminAssigneeControls } from "../../../AdminAssigneeControls";
import { ToolContextMenu, ToolContextMenuItem } from "../../shared/components/ToolContextMenu";
import { AdminActionItemLayout } from "../layouts/AdminActionItemLayout";
import { ActionItemTitleDisplay } from "./ActionItemTitleDisplay";

export type AdminActionItemProps = {
	actionItem: ActionItem;
	containerClassName?: string;
};

export const AdminActionItem: FC<AdminActionItemProps> = ({ actionItem, containerClassName }) => {
	const { user } = useAuthenticatedUser();
	const { toggleCompleted, updateDueDate, updateContent } = useUpdateActionItem();
	const { deleteActionItem } = useDeleteActionItem();
	const { selectedActionItemId } = useActionItemsSelection();
	const { setSelectedActionItemId, setEditActionItem } = useActionItemsSelectionDispatch();
	const isSelected = selectedActionItemId === actionItem.id;

	const content = (
		<>
			<div className="flex flex-col justify-start items-start flex-grow">
				<span className="flex flex-row items-center w-full">
					<ActionItemTitleDisplay actionItem={actionItem} completed={!!actionItem.completedAt} />
				</span>
				<QuickEditDateDisplay
					dueAt={actionItem.dueAt}
					completedAt={actionItem.completedAt}
					onDateChange={(newDate) => updateDueDate(actionItem, newDate)}
				/>
			</div>
			<div className="flex items-center gap-2 justify-center self-start min-w-fit">
				<AdminAssigneeControls
					assignedEntity={{ entityType: "actionItem", entityId: actionItem.id }}
					currentAssignee={actionItem.assigneeId}
				/>
				<ToolContextMenu testId="action-item-context-menu">
					<ToolContextMenuItem
						testId="copy-link"
						onClick={(e) => {
							e.stopPropagation();
							navigator.clipboard.writeText(`${window.location}&actionItemId=${actionItem.id}`);
						}}
						label="Copy Link"
					/>
					<ToolContextMenuItem
						testId="edit-link"
						onClick={(e) => {
							e.stopPropagation();
							setEditActionItem(actionItem);
						}}
						label="Edit"
					/>
					<ToolContextMenuItem
						testId="delete-link"
						onClick={(e) => {
							e.stopPropagation();
							deleteActionItem({
								actionItemId: actionItem.id,
								relation: {
									relationType: actionItem.relationType,
									relationId: actionItem.relationId,
								} as Relations.ActionItems,
							});
						}}
						label="Delete"
					/>
				</ToolContextMenu>
			</div>
		</>
	);

	return (
		<div
			tabIndex={0}
			className="flex w-full"
			onClick={(e) => {
				e.stopPropagation();
				e.preventDefault();

				setSelectedActionItemId(actionItem.id);
			}}
		>
			<AdminActionItemLayout
				statusIcon={
					<CheckMarkBubble
						isComplete={actionItem.status === actionItemStatus.complete}
						toggleComplete={() => {
							toggleCompleted(actionItem, user.id);
						}}
					/>
				}
				content={content}
				containerClassName={cn(
					containerClassName,
					// Hover here only affects the border with updated layout
					"w-full cursor-pointer relative hover:bg-gray-50",
				)}
				footer={
					isSelected &&
					actionItem.content && (
						<QuickEditRichTextDisplay
							content={actionItem.content}
							onConfirm={(newContent) => updateContent(actionItem, newContent)}
						/>
					)
				}
				isSelected={isSelected}
				withHover
			/>
		</div>
	);
};
